import { z } from 'zod';

import { ResponseData } from '@/features/progresses/progresses.types';
import { DropdownOption } from '@/types/generalTypes';

export enum FeedPublishScope {
  Organisation = 0,
  Team = 1
}

export enum SocialEntityHolderType {
  EngagementPost = 1,
  ProgressResponse = 2
}

export interface IUpdateEngagementFlagsDto {
  showGreetingVideoPost?: boolean;
}

export type FeedEngagementUpdatesResponseDto = {
  posts: EngagementUpdateDTO[];
  showGreetingVideoPost: boolean;
  socialFeedFirstEnabledAtUtc?: string;
  hasMore: boolean;
  nextCursorCreatedTimeUtc?: string;
};

export type EngagementUpdateDTO = {
  id: string;
  userId: string;
  userName: string;
  userTitle: string;
  content: string;
  images?: {
    id: string;
    url: string;
  }[];
  supportingInfoSets?: {
    id: string;
    order: number;
    additionalDetails?: string;
    url?: string;
    location?: string;
    latitude?: number;
    longitude?: number;
    images: { id: string; url: string }[];
  }[];
  timeAgo: string;
  feedDisplayTimestamp: string;
  allocatedTo?: string;
  strategyId?: string;
  strategyName?: string;
  deliverableId?: string;
  deliverableName?: string;
  signalId?: string;
  signalName?: string;
  focusArea: DropdownOption;
  url?: string;
  location?: string;
  latitude?: number;
  longitude?: number;
  likeCount: number;
  isLikedByCurrentUser: boolean;
  isCreatedByCurrentUser: boolean;
  canEditByCurrentUser: boolean;
  canDeleteByCurrentUser: boolean;
  commentCount: number;
  feedPublishStatus: string;
  publishScope: FeedPublishScope;
  targetBusinessUnitId?: string;
  businessUnitId?: string;
  businessUnitName?: string;
  businessUnitGroupId?: string;
  businessUnitGroupName?: string;
  feedItemType?: 'engagePost' | 'progressResponse';
  reportTypes?: DropdownOption[];
  businessUnits?: DropdownOption[];
  progressResponse?: ResponseData;
};

export type FeedListItem =
  | { type: 'post'; feedDisplayTimestamp: string; likeCount: number; commentCount: number; post: EngagementUpdateDTO }
  | { type: 'greeting'; feedDisplayTimestamp: string; likeCount: number; commentCount: number }
  | { type: 'joined'; feedDisplayTimestamp: string; likeCount: number; commentCount: number };

export type ReviewPendingUpdatesItem = {
  id: string;
  type: 'engagePost' | 'progressResponse';
};

export type ReviewPendingUpdatesRequest = {
  items: ReviewPendingUpdatesItem[];
  approve: boolean;
  comment?: string;
};

export type ToggleLikeRequest = {
  itemId: string;
  feedItemType: SocialEntityHolderType;
};

export type ToggleLikeResponse = {
  isLiked: boolean;
  likeCount: number;
};

export type AddCommentRequest = {
  itemId: string;
  feedItemType: 'engagePost' | 'progressResponse';
  content: string;
  parentCommentId?: string;
};

export type CommentDTO = {
  id: string;
  itemId: string;
  feedItemType: 'engagePost' | 'progressResponse';
  userId: string;
  userName: string;
  content: string;
  timeAgo: string;
  parentCommentId?: string;
  createdDate: string;
};

export type EngagementLinkPreviewDto = {
  title?: string;
  imageUrl?: string;
  canonicalUrl?: string;
};

export type FocusAreaDueResponseCountDto = {
  id: string;
  title: string;
  icon?: string;
  iconColor?: string;
  textColor?: string;
  backgroundColor?: string;
  dueCount: number;
  distinctSignalCount: number;
  completedDueCount: number;
  dueUnrespondedCount: number;
  totalDueCount: number;
  hasMoreDueResponses: boolean;
  topOverdueResponses: SignalResponseDTO[];
  deliverables: {
    deliverableId: string;
    deliverableName: string;
    order: string;
    assignedSignalCount: number;
    strategyCategory?: DropdownOption;
    progressResponses: SignalResponseDTO[];
    optionalSignals: {
      questionId: string;
      signal: string;
      questionOrder: number;
      type?: string;
      responseOptions: ProgressResponseSignalOptionDto[];
      businessUnits?: DropdownOption[];
      availablePeriods: SignalPeriodDto[];
    }[];
  }[];
};

export type MemberUpdateActivityDto = {
  fromDate: string;
  toDate: string;
  assignedPeriodCount: number;
  respondedCount: number;
  requestedUpdatesReceivedCount: number;
  awaitingUpdateCount: number;
  unpromptedUpdateCount: number;
  previousAssignedPeriodCount: number;
  previousRespondedCount: number;
  previousRequestedUpdatesReceivedCount: number;
  previousAwaitingUpdateCount: number;
  previousUnpromptedUpdateCount: number;
};

export type SignalResponseDTO = {
  focusAreaId: string;
  progressResponseId: string;
  questionId: string;
  businessUnitId: string;
  businessUnitName?: string;
  signal: string;
  questionOrder: number;
  strategyName: string;
  scheduledPeriod: string;
  status?: number;
  type?: string;
  responseOptions: ProgressResponseSignalOptionDto[];
  businessUnits?: DropdownOption[];
  availablePeriods: SignalPeriodDto[];
};

export type SignalPeriodDto = {
  progressResponseId: string;
  businessUnitId: string;
  scheduledPeriod: string;
};

export type FocusAreaAllResponsesDto = {
  focusAreaId: string;
  focusAreaTitle: string;
  totalDueCount: number;
  strategies: {
    strategyId: string;
    strategyName: string;
    signals: {
      questionId: string;
      signal: string;
      type?: string;
      timeSlots: SignalResponseDTO[];
    }[];
  }[];
};

export type ProgressResponseSignalOptionDto = {
  id: string;
  title: string;
};

export type NewUpdateMode = 'text' | 'photo' | 'location';

export type UpdateTargetDto = {
  targetType: 'deliverable' | 'signal';
  focusAreaId: string;
  focusAreaName: string;
  strategyId: string;
  strategyName: string;
  deliverableId: string;
  deliverableOrder: string;
  deliverableName: string;
  questionId?: string;
  questionName?: string;
  type?: string;
  responseOptions: ProgressResponseSignalOptionDto[];
  businessUnits: DropdownOption[];
  availablePeriods: SignalPeriodDto[];
};

export type SocialFeedSortType = 'Latest' | 'Oldest' | 'Most Popular';

export type SocialFeedFilterItem = DropdownOption & {
  type: 'focusArea' | 'team' | 'group' | 'timePeriod' | 'reportType';
};

export type AudienceSubView = 'main' | 'team' | 'group';

export const socialFeedSearchSchema = z.object({
  signal: z.string().optional(),
  responseId: z.string().optional(),
  isEngagement: z.boolean().optional()
});

export const singleUpdateSearchSchema = z.object({
  type: z.string().optional()
});
